import {
  PureSheetValidationResult,
  SheetValidationContext,
  SheetValidator,
} from '../sheet-validator';
import { differsAtCentPrecision } from '../validation-field-key.util';
import {
  buildRole12ColumnKey,
  buildRole18Key,
  buildRole19CandidateKeys,
  getRole12ValidatingControls,
  isRole12CalculatedControl,
  isRole12Empty,
  isRole12SectionHeader,
  pickRole12Schema,
  recomputeRole12WithRole13Inheritance,
  Role12Schema,
  Role12SchemaControl,
} from './role12-statement-of-profit-or-loss.rules';
import {
  readSlice,
  resolveDimensionVisibility,
  VisibilityCell,
} from './dimension-visibility.util';
import { getDimensionVisibilityConfig } from './dimension-visibility-registry';
import {
  accountingStandardToXbrlPrefix,
  calculator,
  hasCalculatedFieldMismatch,
  isResolvedAccountingStandard,
} from './calc-diff.helper';
import { ROLE13_ANALYSIS_LINK_ROLE } from './link-roles';
import { SHEET_KEYS } from '../../../models/sheet-keys';

/**
 * Role code Role-12 controls carry in `ui.linkTo.role` when they inherit their
 * value from the Role-13 (Analysis of Profit or Loss) sheet. Mirrors the
 * editor's pullRole13Values() filter (`control.ui.linkTo.role === '310100'`).
 * The Revenue / CostOfSales / OtherIncome / OtherExpenseByFunction /
 * FinanceIncome leaves carry this link; the `ProfitLoss` control's `410000`
 * link is navigation-only in THIS direction (it is a calculated total, never
 * value-inherited FROM role 15). The value travels the other way: role 15's own
 * `Profit (loss)` declares `ui.linkTo {role: '310000', navigate: false}` and
 * inherits this sheet's total — see `ROLE12_PROFIT_OR_LOSS_LINK_ROLE`.
 *
 * Single-sourced in `./link-roles.ts` and re-exported here so the mounted
 * cross-sheet recompute util shares the exact same literal without importing
 * this large validator module.
 */
export { ROLE13_ANALYSIS_LINK_ROLE };

/**
 * Pure validator for Role 12 (Statement of Profit or Loss).
 *
 * Mirrors the editor's role12-310000-statement-of-profit-or-loss.component.ts
 * `evaluateSheetStatus()` surface — i.e. which rows resolve to the 'Failed'
 * mapping status, which is the SOLE driver of the persisted
 * `hasErrors` flag on `SheetLevelStatusData`. The component publishes
 *
 *   this.stateService.updateSheetLevelStatus('statementOfProfitOrLoss',
 *                                            { hasErrors });
 *
 * where `hasErrors` becomes true iff at least one schema row's
 * `getRowValidationStatus()` returns 'Failed'. A row is 'Failed' when ANY of:
 *
 *  (a) `validationErrors` has an entry for the row that is NOT purely an
 *      API mismatch ("does not match the extracted sum") — i.e. a "real"
 *      error (cross-sheet equality, ProfitLoss <-> ProfitLoss_2 mismatch,
 *      mandatory-empty, conditional-mandatory-empty).
 *  (b) `equalityValidationErrors` has any entry for the row — same source
 *      as (a)'s cross-sheet branch but separately tracked for red borders.
 *  (c) `mandatoryFieldErrors` has the qname.
 *  (d) Any of the row's four FormControls is invalid AND dirty/touched
 *      (cannot fire from the orchestrator's persisted-payload path —
 *      dirty/touched are runtime-only — so we treat this branch as
 *      unreachable in pure mode, matching role 15 / role 23 lifts).
 *  (e) Schema marks the row required AND all four columns are empty.
 *
 * Failed drivers actually mirrored here:
 *
 *  1. **Static required fields** (`required: true` in the schema) —
 *     mirrors `validateMandatoryFields()`. Uses LB-001 fix-guard pattern
 *     so a future schema edit declaring requiredness via
 *     `validators: [{ name: 'required' }]` is also picked up. Today both
 *     MFRS and MPERS schemas only use the top-level flag; LB-013-style
 *     fix-guard preserved for safety.
 *
 *  2. **Conditional required fields** (`requiredIf`) — mirrors
 *     `validateConditionalMandatoryFields()`. Two attribution rows in the
 *     MFRS schema (`ProfitLossAttributableToOwnersOfParent`,
 *     `ProfitLossAttributableToNoncontrollingInterests`) become required
 *     across visible columns when `filingPrerequisites
 *     NatureOfFinancialStatements === 'Consolidated'`.
 *
 *  3. **Same-sheet ProfitLoss <-> ProfitLoss_2 cross-validation** — both
 *     fields are calculated in `CalculationService.calculateTotals_310000`
 *     from disjoint inputs (continuing-vs-discontinued operations sum
 *     vs. attribution sum). A mismatch at cent precision
 *     indicates the user's attribution inputs do not roll up to the
 *     calculated profit total. Mirrors the editor's `validateAgainstApiData()`
 *     "Cross-validation" branch.
 *
 *  4. **Cross-sheet ProfitLossBeforeTax equality vs Role 18** (cash flows
 *     indirect). Schema declares `validation.equality.sheet =
 *     statementOfCashFlowsIndirectMethod`. Role 18 is a `dualColumn` sheet
 *     so the lookup uses the plain `qname[Dim][Period]` shape.
 *
 *  5. **Cross-sheet ProfitLoss equality vs Role 19** (SOCIE). Schema
 *     declares `validation.equality.sheet = statementOfChangesInEquity`.
 *     Role 19 stores values with equity-axis suffixes — the lookup probes
 *     EquityMember / EquityAttributableToOwnersOfParentMember / legacy
 *     dual-column candidates in order, matching the validation-warning
 *     service's `buildFieldKey()` for `equityColumns` sheets and the role
 *     15 lift.
 *
 * Drivers intentionally NOT mirrored (would not change `hasErrors`):
 *
 *  - API-mismatch warnings ("does not match the extracted sum") → these
 *    are Suggested Change category, not Failed. The editor does not flip
 *    `hasErrors` for them; the pure validator reports
 *    `hasSuggestedChanges: false` to match — the component never publishes
 *    the suggested-change flag for this sheet.
 *  - Form-control `invalid && (dirty || touched)` → unreachable in pure
 *    mode (no FormControl exists when validating a persisted payload).
 *
 * ---- Cross-sheet posture / dependency graph ------------------------------
 *
 * Per `SheetDependencyService`:
 *   statementOfProfitOrLoss -> [statementOfCashFlowsIndirectMethod]
 *   analysisOfProfitOrLoss  -> [statementOfProfitOrLoss]
 *
 * i.e. role 12 INVALIDATES role 18 (when 12 changes, 18's cache is dropped),
 * and role 13 INVALIDATES role 12. The equality rule on
 * `ProfitLossBeforeTax` makes role 18 our cross-sheet *read* — when role 18
 * is also lifted (Batch 2), the orchestrator's bundle will include its
 * slice automatically. Role 12 must NOT call into role 18's validator
 * directly; we read its data only via `context.crossSheetData`.
 *
 * Role 13's contribution is leaf inheritance: the editor's `pullRole13Values()`
 * overwrites Role 12's Role-13-linked leaves (Revenue, CostOfSales, OtherIncome,
 * OtherExpenseByFunction, FinanceIncome) with Role 13's values BEFORE it calls
 * calculateTotals() + `evaluateSheetStatus()`. The headless path therefore MUST
 * read role 13 from `crossSheetData` and replay that inheritance before
 * recomputing — see `recomputeRole12WithRole13Inheritance` in validate(). (An earlier version
 * of this validator assumed Role 13's values were "already in sheetData" and
 * skipped the read; that holds for the mounted editor's own live payload but NOT
 * for the headless listing slice, which is the raw N8N extraction without the
 * fresh pull — so a Role-13 value that disagreed with Role 12's persisted leaf
 * flipped the sheet status from a green tick on the listing to a red cross on
 * open.) Role 8 (statementOfFinancialPosition) is not referenced by any role 12
 * Failed driver and is still not read here.
 *
 * The validator reads `context.crossSheetData` defensively and tolerates
 * absent slices (orchestrator may pass `{}` or omit a key) per the Tier 4
 * task contract. Each cross-sheet check degrades gracefully:
 *
 *   • filingPrerequisites absent → visibility defaults to "all four columns
 *     visible", Nature defaults to "not Consolidated" so requiredIf does
 *     NOT fire (this differs from role 15 which defaults to firing — role
 *     12's component checks `dependentValue !== equals` and treats unknown
 *     as not-equal, which means the conditional check does not arm. We
 *     mirror that to preserve parity).
 *   • Role 18 absent → ProfitLossBeforeTax equality skipped (no flagging
 *     when there is nothing to compare against).
 *   • Role 19 absent → ProfitLoss equality skipped (same reason).
 *   • Role 19 PRESENT but the specific row missing → equality also skipped
 *     (first-listing-visit hazard: SOCIE has not been opened yet, so its
 *     payload predates the equity-axis row this rule references). Mirrors
 *     the slice-level skip but at row granularity.
 */

interface Role12FailedRowAccumulator {
  errors: Record<string, string>;
  failedRowQnames: Set<string>;
}

/**
 * Human label for a visibility cell, used in error messages. The shared
 * `VisibilityCell` (from dimension-visibility.util) intentionally drops the
 * per-cell `label` the former role-local `Role12ColumnVisibility` carried, so
 * we derive the identical string here — `"<Dimension> <Current|Previous>"`,
 * matching the component's `columnsToValidate` labels (e.g. "Consolidated
 * Current", "Separate Previous").
 */
function columnLabel(cell: VisibilityCell): string {
  const periodWord = cell.period === 'CurrentYear' ? 'Current' : 'Previous';
  return `${cell.dimension} ${periodWord}`;
}

function readCrossSheetSlice(
  context: SheetValidationContext,
  sheet: string
): Record<string, any> | undefined {
  const bundle = context.crossSheetData;
  if (!bundle) return undefined;
  const slice = bundle.get(sheet as Parameters<typeof bundle.get>[0]);
  return slice as Record<string, any> | undefined;
}

/* ------------------------------------------------------------------------ */
/* LB-013-style fix-guard: required-by-validators-array detection           */
/* ------------------------------------------------------------------------ */

interface SchemaValidatorEntry {
  name?: string;
  conditionalOn?: unknown;
}

interface SchemaControlForRequired {
  required?: boolean;
  validators?: SchemaValidatorEntry[];
}

/**
 * LB-001 fix-guard: a control is statically required when EITHER
 * `required === true` OR the validators array contains an unconditional
 * `{ name: 'required' }` entry. Today neither role 12 schema declares a
 * `validators` array, but carrying the guard means a future schema edit is
 * picked up automatically — same posture as roles 6, 13, 21, 22.
 */
function hasUnconditionalRequiredViaValidators(
  control: SchemaControlForRequired
): boolean {
  return (
    control.validators?.some(
      (validator) => validator?.name === 'required' && validator?.conditionalOn === undefined
    ) ?? false
  );
}

function isStaticallyRequired(control: Role12SchemaControl): boolean {
  return (
    control.required === true ||
    hasUnconditionalRequiredViaValidators(control as SchemaControlForRequired)
  );
}

/* ------------------------------------------------------------------------ */
/* (1) Static required-field check                                          */
/* ------------------------------------------------------------------------ */

/**
 * Mirrors `validateMandatoryFields()`. For every schema control with
 * `required: true` (or LB-001 validators-array form), check the value at
 * each visible column: if `value === null` (the editor's exact guard, which
 * we broaden to undefined / '' for orchestrator payloads), flag the cell.
 *
 * The component flags by adding errors PER COLUMN to validationErrors and
 * adds the qname to mandatoryFieldErrors — which getRowValidationStatus()
 * then surfaces as 'Failed' for the entire row. We replicate the row-level
 * flag (the validator's `errors` map is diagnostic; `hasErrors` is the
 * consumer-facing flag).
 */
function checkStaticRequiredErrors(
  schema: Role12Schema,
  sheetData: Record<string, any>,
  visibleColumns: ReadonlyArray<VisibilityCell>,
  acc: Role12FailedRowAccumulator
): void {
  for (const control of getRole12ValidatingControls(schema)) {
    if (!isStaticallyRequired(control)) continue;
    // Calculated/readonly rows are filled in by CalculationService — skip
    // them so the validator does not flag emptiness on totals.
    if (isRole12CalculatedControl(control)) continue;

    for (const column of visibleColumns) {
      if (!column.visible) continue;
      const key = buildRole12ColumnKey(control.qname, column.dimension, column.period);
      if (isRole12Empty(sheetData[key])) {
        acc.errors[key] = `${columnLabel(column)}: This is a mandatory field.`;
        acc.failedRowQnames.add(control.qname);
      }
    }
  }
}

/* ------------------------------------------------------------------------ */
/* (2) requiredIf cross-sheet conditional check                             */
/* ------------------------------------------------------------------------ */

/**
 * Mirrors `validateConditionalMandatoryFields()`. For every schema control
 * with a `requiredIf` rule, look up the referenced sheet+field in
 * crossSheetData. When that value matches `equals`, every visible column
 * must carry a non-null value (zero is acceptable — the editor's check is
 * `=== null`).
 *
 * IMPORTANT: when the cross-sheet slice is missing (bundle empty,
 * orchestrator did not load filingPrerequisites yet), the editor's
 * `dependentValue !== equals` evaluates to `undefined !== 'Consolidated' →
 * true`, so the rule SKIPS — the field is NOT flagged. Mirror that exactly.
 * Defaulting to "required" here would force-fail conversions whose
 * filingPrerequisites had not yet been loaded.
 */
function checkConditionalRequiredErrors(
  schema: Role12Schema,
  sheetData: Record<string, any>,
  context: SheetValidationContext,
  visibleColumns: ReadonlyArray<VisibilityCell>,
  acc: Role12FailedRowAccumulator
): void {
  for (const control of schema.controls) {
    if (isRole12SectionHeader(control)) continue;
    if (!control.requiredIf) continue;
    if (isRole12CalculatedControl(control)) continue;

    const { sheet, field, equals, errorMessage } = control.requiredIf;
    const refSlice = readCrossSheetSlice(context, sheet);
    // Mirror the component's `sheetData?.[field]` lookup: undefined when
    // either the slice or the field is missing. The subsequent `!== equals`
    // check will then resolve to `true`, causing the rule to skip.
    const refValue = refSlice?.[field];
    if (refValue !== equals) continue;

    for (const column of visibleColumns) {
      if (!column.visible) continue;
      const key = buildRole12ColumnKey(control.qname, column.dimension, column.period);
      if (isRole12Empty(sheetData[key])) {
        acc.errors[key] = `${columnLabel(column)}: ${errorMessage ?? `${control.qname} is required`}`;
        acc.failedRowQnames.add(control.qname);
      }
    }
  }
}

/* ------------------------------------------------------------------------ */
/* (3) Same-sheet ProfitLoss <-> ProfitLoss_2 cross-validation              */
/* ------------------------------------------------------------------------ */

function getProfitLossPair(
  accountingStandard: string | undefined | null
): { profitLoss: string; profitLoss2: string } {
  const prefix =
    accountingStandard ===
    'Malaysian Financial Reporting Standards'
      ? 'ifrs-full'
      : 'ifrs-smes';
  return {
    profitLoss: `${prefix}:ProfitLoss`,
    profitLoss2: `${prefix}:ProfitLoss_2`,
  };
}

/**
 * Mirrors the editor's "Cross-validation: ProfitLoss vs ProfitLoss_2" branch
 * inside `validateAgainstApiData()`. Both values are calculated; a mismatch
 * means the user's attribution rows (Owners + Equity-other + NCI) do not sum
 * to the continuing+discontinued profit total. Uses
 * {@link differsAtCentPrecision} — round both to integer cents, then `!==` —
 * mirroring the editor's `Math.round(v*100)/100` then `!==`. (The earlier
 * `comparisonDifference > MBRS_EQUALITY_TOLERANCE` form only flagged a
 * difference of ≥ 0.02, so a one-cent mismatch the editor flags slipped
 * through; cent comparison closes that gap and avoids float round-trip drift.)
 */
function checkProfitLossPairCrossValidation(
  sheetData: Record<string, any>,
  context: SheetValidationContext,
  visibleColumns: ReadonlyArray<VisibilityCell>,
  acc: Role12FailedRowAccumulator
): void {
  const { profitLoss, profitLoss2 } = getProfitLossPair(context.accountingStandard);

  for (const column of visibleColumns) {
    if (!column.visible) continue;
    const plKey = buildRole12ColumnKey(profitLoss, column.dimension, column.period);
    const pl2Key = buildRole12ColumnKey(profitLoss2, column.dimension, column.period);

    if (differsAtCentPrecision(sheetData[plKey], sheetData[pl2Key])) {
      // Mirror the component's two error messages so any downstream
      // diagnostic capture sees the same text. Both rows are flagged
      // because both contribute to the row-level Failed status.
      acc.errors[plKey] =
        `${columnLabel(column)}: Mismatch in value provided for the same field. ` +
        `The value should match Total Profit (loss) in Statement of Profit or Loss.`;
      acc.errors[pl2Key] =
        `${columnLabel(column)}: Mismatch in value provided for the same field. ` +
        `The value should match Profit (loss) in Statement of Profit or Loss.`;
      acc.failedRowQnames.add(profitLoss);
      acc.failedRowQnames.add(profitLoss2);
    }
  }
}

/* ------------------------------------------------------------------------ */
/* (4 + 5) Cross-sheet equality rules from schema                           */
/* ------------------------------------------------------------------------ */

/**
 * Generic schema-driven equality check. For every control with a
 * `validation.equality` rule that targets ANOTHER sheet (sheet !==
 * 'statementOfProfitOrLoss'), compare the local value against the
 * referenced sheet's value across the four visible columns. A mismatch
 * above cent precision (any ≥ 0.01 difference) flags the row Failed.
 *
 * Lookup shape depends on the target sheet:
 *   • Role 18 (statementOfCashFlowsIndirectMethod) — dualColumn,
 *     `qname[Dim][Period]`.
 *   • Role 19 (statementOfChangesInEquity) — equityColumns, probe the
 *     candidate keys returned by buildRole19CandidateKeys (first match
 *     wins).
 *   • Other sheets — fall back to dualColumn shape; if a future schema
 *     edit references a sheet with a different layout the validation-
 *     warning service also relies on SHEET_COLUMN_TYPE_MAP, so this
 *     fallback matches the service's `if (!type) return fieldQname`
 *     branch.
 *
 * Both sides being absent skips the check (matches the editor's
 * "skip if both null" branch). When only ONE side is null/empty, the
 * editor coerces the missing side to 0 and flags if the other has a
 * non-trivial value — we mirror that.
 *
 * NOTE: this purposely IGNORES same-sheet equality rules (e.g.
 * ProfitLoss <-> ProfitLoss_2) because those are handled by the
 * dedicated branch above with the editor's specific 0.01 tolerance and
 * row-pair flagging. Same-sheet rules are detected via `rule.sheet ===
 * 'statementOfProfitOrLoss'`.
 */
function checkCrossSheetEqualityRules(
  schema: Role12Schema,
  sheetData: Record<string, any>,
  context: SheetValidationContext,
  visibleColumns: ReadonlyArray<VisibilityCell>,
  acc: Role12FailedRowAccumulator
): void {
  for (const control of schema.controls) {
    if (isRole12SectionHeader(control)) continue;
    const rules = control.validation?.equality;
    if (!rules || rules.length === 0) continue;

    for (const rule of rules) {
      // Skip same-sheet rules — handled by checkProfitLossPairCrossValidation.
      if (rule.sheet === SHEET_KEYS.statementOfProfitOrLoss) continue;
      // Schema-driven taxonomy filter — mirror the validation-warning
      // service's check.
      if (
        rule.taxonomy &&
        rule.taxonomy.toUpperCase() !==
          (context.accountingStandard ===
          'Malaysian Financial Reporting Standards'
            ? 'MFRS'
            : 'MPERS')
      ) {
        continue;
      }

      const refSlice = readCrossSheetSlice(context, rule.sheet);
      // When the orchestrator did not bundle the target sheet, skip the
      // equality check entirely — there is nothing to compare against and
      // flagging would be a false positive. Same posture as role 15 with
      // an absent role 19 slice.
      if (!refSlice) continue;

      for (const column of visibleColumns) {
        if (!column.visible) continue;

        const localKey = buildRole12ColumnKey(control.qname, column.dimension, column.period);
        const localRaw = sheetData[localKey];

        // Resolve the reference cell. Try the equity-axis candidates first
        // for any role 19 reference, otherwise fall back to dualColumn.
        //
        // `referenceRowPresent` distinguishes "row missing entirely from the
        // target sheet" (treat as unhydrated, skip the check) from "row
        // present but empty/zero" (compare against 0 as the editor does).
        // This guards the first-listing-visit hazard: when role 19 has not
        // been visited, its slice may be present but lack rows for fields
        // role 12 calculates fresh on the listing path. Coercing the absent
        // ref to 0 and flagging "mismatch" is a false positive — the user
        // never had the chance to populate role 19. Same defensive posture
        // as `if (!refSlice) continue` above, scoped to the row level.
        let refRaw: unknown;
        let refKey = '';
        let referenceRowPresent = false;
        if (rule.sheet === SHEET_KEYS.statementOfChangesInEquity) {
          // Role 19 (SOCIE) persists with an equity-axis suffix per
          // `buildRole19EquityColumnKey` in role19-statement-of-changes-in-
          // equity.rules.ts (line 356): `{rowQname}:{columnQname}[{Cons}]
          // [{Period}]`. The component's totalColumnQname for ProfitLoss is
          // `ifrs-full:EquityMember` (or the parent-attribution member for
          // consolidated breakdown), so the candidate order below matches
          // the role 15 lift verbatim. Legacy dual-column kept last for any
          // back-compat payload that pre-dates the equity-axis migration.
          const candidates = buildRole19CandidateKeys(
            rule.field,
            column.dimension,
            column.period,
            context.accountingStandard
          );
          for (const candidate of candidates) {
            if (candidate in refSlice) {
              refRaw = refSlice[candidate];
              refKey = candidate;
              referenceRowPresent = true;
              break;
            }
          }
        } else {
          // Default: dualColumn lookup. Covers role 18 explicitly and any
          // future dualColumn cross-sheet reference.
          refKey = buildRole18Key(rule.field, column.dimension, column.period);
          referenceRowPresent = refKey in refSlice;
          refRaw = refSlice[refKey];
        }

        const localEmpty = isRole12Empty(localRaw);
        const refEmpty = isRole12Empty(refRaw);

        // Skip when both sides are empty — no data to compare. Mirrors the
        // editor's "skip if both null" branch.
        if (localEmpty && refEmpty) continue;

        // First-visit hazard: target sheet was bundled (refSlice exists) but
        // does NOT carry the referenced row at all. Most commonly because
        // the user has not yet opened that sheet's editor and its on-disk
        // payload predates the introduction of this row. Coercing to 0
        // would manufacture a false mismatch against role 12's calculated
        // total. Skip — when the user does visit the other sheet, its
        // hydration will publish the row and the check will fire normally.
        if (!referenceRowPresent) continue;

        // Mirror the editor's coercion: empty side treated as 0 (these are
        // calculated totals; a missing total is meaningfully zero).
        const localNum = localEmpty ? 0 : parseFloat(String(localRaw));
        const refNum = refEmpty ? 0 : parseFloat(String(refRaw));

        const safeLocal = Number.isFinite(localNum) ? localNum : 0;
        const safeRef = Number.isFinite(refNum) ? refNum : 0;

        if (differsAtCentPrecision(safeLocal, safeRef)) {
          acc.errors[localKey] =
            rule.message ??
            `${columnLabel(column)}: Mismatch in value provided for the same field. ` +
              `The value should match ${rule.field} in ${rule.sheet}.`;
          acc.failedRowQnames.add(control.qname);
        }
      }
    }
  }
}

/* ------------------------------------------------------------------------ */
/* Public validator                                                         */
/* ------------------------------------------------------------------------ */

export const role12StatementOfProfitOrLossValidator: SheetValidator = {
  sheetKey: SHEET_KEYS.statementOfProfitOrLoss,
  // Tier 4: cross-sheet reads (filingPrerequisites + role 18 + role 19) AND
  // schema-driven calculations participate in the Failed drivers (the
  // ProfitLoss <-> ProfitLoss_2 same-sheet check operates on calculated
  // totals; the ProfitLossBeforeTax cross-sheet equality compares two
  // calculation outputs from different sheets). Dual-path parity-harness
  // mandatory in dev.
  tier: 4,
  validate(sheetData: Record<string, any>, context: SheetValidationContext): PureSheetValidationResult {
    const schema = pickRole12Schema(context.accountingStandard);
    // Column visibility comes from the SHARED, config-driven resolver
    // (dimension-visibility.util + registry) — the same one the mounted editor
    // calls via CrossReferenceService.getVisibility(), so the on-screen columns
    // and the validated columns cannot drift. The resolver reads the two DEI
    // gating fields (`DisclosureOfFinancialStatementsPreparationForCurrentSubmission`
    // and `NatureOfFinancialStatements`) prerequisites-first, then filingInfo —
    // the first-listing-visit fallback (mirror of the role 7 fix): the editor
    // has not mounted, so `filingPrerequisites` is `undefined` until the user
    // opens the conversion, but both fields are also persisted on the backend's
    // `filingInfo` slice (role-1.form.json lines 113 / 279). Without it the
    // listing path surfaces false "PreviousYear is mandatory" errors against any
    // first-time-preparation conversion.
    const visibleColumns = resolveDimensionVisibility(
      getDimensionVisibilityConfig(SHEET_KEYS.statementOfProfitOrLoss)!,
      {
        filingPrerequisites: readSlice(context.crossSheetData, 'filingPrerequisites'),
        filingInfo: readSlice(context.crossSheetData, 'filingInfo'),
      }
    );

    const acc: Role12FailedRowAccumulator = {
      errors: {},
      failedRowQnames: new Set<string>(),
    };

    // Build the equality operands the SAME way the mounted editor does, by
    // replaying its two pre-validation transforms on Role-12's leaves:
    //
    //   1. Role-13 inheritance — pullRole13Values() overwrites Role-12's
    //      Role-13-linked leaf line items (Revenue, CostOfSales, OtherIncome,
    //      OtherExpenseByFunction, FinanceIncome) with Role-13's current
    //      Analysis-of-P&L values BEFORE anything else. A Role-13 value that
    //      disagrees with Role-12's persisted leaf shifts the recomputed
    //      ProfitLoss / ProfitLoss_2 / ProfitLossBeforeTax, which can break the
    //      same-sheet attribution check and the cross-sheet equality tie-outs
    //      (vs Role 18 / Role 19). Recomputing from Role-12's persisted slice
    //      alone hid that shift — the listing showed a green tick while opening
    //      the sheet (which inherits first) showed a red cross. (This corrects
    //      the prior "Role 13 is already in sheetData, so we don't read it"
    //      assumption above: that holds for the mounted editor's own payload,
    //      but NOT for the headless listing slice which is the raw extraction.)
    //   2. calculateTotals() — recompute the calculated totals from the (now
    //      inherited) leaves and overlay them, so the equality layers (3)+(4/5)
    //      below compare freshly-summed totals rather than the raw extraction.
    //
    // The original `sheetData` is preserved for the required-field layers and
    // the Validated* diff (both of which must see the raw N8N extraction).
    //
    // IMPORTANT — recompute Role-13 BEFORE inheriting. The mounted editor never
    // inherits Role-13's RAW slice: filing-form-container recomputes Role-13's
    // totals at load (calculateTotals_310100) and persists them, so
    // pullRole13Values pulls the RECOMPUTED totals. When Role-13 has an internal
    // extracted-vs-sum mismatch, only the recomputed total shifts Role-12's
    // equality operands — the editor raises a red cross while the headless path,
    // inheriting the raw extraction, stays balanced and publishes a false
    // `Success*`. Replay the SAME Role-13 recompute here so the inherited
    // operands match the editor's (mirrors the Role-8 ← Role-9 fix and the
    // recomputeReferencedSheets step used for the Role-18/19 references).
    //
    // The recipe itself lives in `recomputeRole12WithRole13Inheritance` (rules
    // file) because Role 15's validator needs the identical replay — Role 15's
    // `Profit (loss)` inherits from THIS sheet, so it must see the same
    // Role-13-inherited operands rather than Role 12's raw slice.
    const xbrlPrefix = accountingStandardToXbrlPrefix(
      context.accountingStandard
    );
    const { recomputed: equalityRecomputed, snapshot: equalitySnapshot } =
      recomputeRole12WithRole13Inheritance(
        sheetData,
        readCrossSheetSlice(context, SHEET_KEYS.analysisOfProfitOrLoss),
        xbrlPrefix,
        context.accountingStandard
      );

    // GLOBAL RULE (PO, 2026-06-19, REVISED) — BLOCKING equality compares only
    // live data, NEVER the N8N snapshot.
    //
    // The blocking layers (3)+(4/5) below run against `equalitySnapshot` — the
    // recompute of the CURRENT (Role-13-inherited) leaves, i.e. recompute-of-
    // latest. The N8N extracted snapshot is NOT an operand here: per the PO rule
    // the snapshot is only ever compared against the recompute/latest of the
    // SAME field, and that comparison is the non-blocking `Validated*` Suggested
    // Change surfaced further below (via `calcDiffRecomputed` vs the snapshot).
    //
    // An earlier revision anchored these operands to the snapshot (recompute the
    // baseline's own leaves, then let the extracted totals win). That let an
    // INTERNALLY-INCONSISTENT extraction — one whose stored total disagrees with
    // its own leaves (Perm 04: stored ProfitLoss 195.08 while the leaves recompute
    // to 196.60 = Σ attribution) — drive a false block, because the stale stored
    // total was compared against a freshly-recomputed sibling field. The editor
    // never does that: it recomputes every total from the leaves before checking.
    // Comparing recompute-of-latest vs recompute-of-latest mirrors the editor and
    // still blocks a GENUINE leaf-level imbalance (where the recomputed totals
    // themselves disagree).
    // -----------------
    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // Diagnostic logs — kept while we reconfirm that the extraction-vs-calc gap
    // (e.g. ifrs-full:Assets persisted vs recomputed) lands in hasSuggestedChanges
    // and NOT hasErrors. Remove once the values are confirmed across a sample.
    // console.log('[On Role 12 Profit Or Loss By Function | Validation]: Equality Params (Schema): ', schema);
    // console.log('[On Role 12 Profit Or Loss By Function | Validation]: Equality Params (Snapshot): ', equalitySnapshot);
    // console.log('[On Role 12 Profit Or Loss By Function | Validation]: Equality Params (Columns): ', visibleColumns);
    // -----------------
    // (1) Static required fields (with LB-001 fix-guard). Raw payload: the
    // editor validates mandatory presence on the entered values.
    checkStaticRequiredErrors(schema, sheetData, visibleColumns, acc);

    // (2) Conditional required fields (requiredIf cross-sheet). Raw payload.
    checkConditionalRequiredErrors(schema, sheetData, context, visibleColumns, acc);

    // (3) Same-sheet ProfitLoss <-> ProfitLoss_2 cross-validation — recompute-of-latest.
    checkProfitLossPairCrossValidation(equalitySnapshot, context, visibleColumns, acc);

    // (4 + 5) Schema-driven cross-sheet equality (Role 18 / Role 19) — recompute-of-latest.
    checkCrossSheetEqualityRules(schema, equalitySnapshot, context, visibleColumns, acc);

    const hasErrors = acc.failedRowQnames.size > 0 || Object.keys(acc.errors).length > 0;
    // -----------------
    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log('[On Role 12 Profit Or Loss By Function | Validation]: Has Errors: ', hasErrors);
    // console.log('[On Role 12 Profit Or Loss By Function | Validation]: Failed Row Qnames: ', acc.failedRowQnames);
    // -----------------
    if (hasErrors) {
      return {
        hasErrors: true,
        hasSuggestedChanges: false,
        errors: acc.errors,
      };
    }

    // AS-842 calc-diff: surface the editor's `Validated*` signal in the silent
    // path by diffing recomputed totals against the immutable N8N extraction;
    // any mismatch flips `hasSuggestedChanges`, which the resolver rolls up to
    // `Success*`.
    //
    // The COMPARISON TARGET stays the raw extraction — `extractedBaseline`
    // (immutable N8N snapshot) when present, else the persisted slice.
    //
    // The RECOMPUTE SOURCE must mirror the mounted editor's
    // `validateAgainstApiData`, which runs AFTER `pullRole13Values()` — i.e. on
    // the Role-13-INHERITED leaves, not Role-12's raw persisted slice. At
    // conversion load the persisted slice can hold a Role-13-linked leaf that is
    // stale relative to Role-13's recompute, so recomputing the asterisk from
    // that raw slice raised a transient `Success*` on the sheet-selector dropdown
    // that vanished the instant the sheet was opened — with no parity warning.
    // Reuse `equalityRecomputed` (recompute from the inherited leaves, already
    // built above) so the load-time verdict matches what the user sees on open.
    // The Role-12 analogue of the Role-8 ← Role-9 asterisk fix.
    //
    // BUT only when we have the snapshot to diff against: without it (the
    // headless listing has no snapshot) the target falls back to the persisted
    // slice, so the source must fall back with it — diffing inherited totals
    // against the stale persisted slice would MANUFACTURE a false asterisk on the
    // listing. Source and target flip together.
    const calcDiffRecomputed = context.extractedBaseline
      ? equalityRecomputed
      : calculator.calculateTotals_310000(sheetData, xbrlPrefix);
    return {
      hasErrors: false,
      // Guard: suppress the calc-diff asterisk while the taxonomy is unresolved
      // (empty accountingStandard defaults to the MFRS prefix → false `Success *`
      // on MPERS data). See isResolvedAccountingStandard.
      hasSuggestedChanges:
        isResolvedAccountingStandard(context.accountingStandard) &&
        hasCalculatedFieldMismatch(context.extractedBaseline ?? sheetData, calcDiffRecomputed),
      errors: acc.errors,
    };
  },
};
